import cn from 'classnames';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Route, useRouteMatch } from 'react-router-dom';

import { useSurveyParamsAdapter } from '@webapp/account/src/components/survey/editor/create/question/preview/survey-adapter';
import { AppliedFilters } from '@webapp/account/src/components/survey/results/applied-filters';
import { ResultsLayoutHeader } from '@webapp/account/src/components/survey/results/layout/header';
import { TEST_QUESTIONS } from '@webapp/account/src/components/survey/results/lib';
import { ResultsContext } from '@webapp/account/src/pages/survey/results/context'; // TODO implement local context
import { formatDateForFilters } from '@webapp/account/src/resources/lib';
import {
    Actions,
    getCrossTabList,
    getQuestionsForReports,
    getWordStat,
    selectReports,
    selectReportsQuestions,
    selectResponsesLimit,
} from '@webapp/account/src/resources/reports';
import { getComparisonWordStat } from '@webapp/account/src/resources/reports/async-comparison';
import {
    selectComparisonEnabled,
    selectFetchPublic,
    selectFiltersVisible,
    selectShareSettings,
    selectWithFiles,
} from '@webapp/account/src/resources/reports/selectors';
import { selectActiveSurvey, selectMain } from '@webapp/account/src/resources/survey/selectors';

import { NotFound } from '@webapp/common/assets/icons';
import { CustomStylesCtx, customStylesInitial } from '@webapp/common/components/custom-styles';
import { Loader } from '@webapp/common/components/loader';
import type { SlibTabsItems } from '@webapp/common/components/slim-tabs';
import { SlimTabs } from '@webapp/common/components/slim-tabs';
import { useAction, useAsync } from '@webapp/common/hooks/use-actions';
import { useDeepSelector } from '@webapp/common/hooks/use-deep-selector';
import { lazy } from '@webapp/common/lib/lazy';
import { QuestionType, TypesWithScore } from '@webapp/common/resources/survey';

import { PublicResultsRoutes, singleRoutes } from '@webapp/public-results/src/results-routes';

import css from './public.css';

const PublicCommon = lazy(() => import('./common'));
const PublicSingle = lazy(() => import('./single'));

const CLOUD_QUESTION_TYPES = [QuestionType.SHORT_TEXT, QuestionType.LONG_TEXT, QuestionType.TEST_TEXT];

export const PublicResultsContent: FC = () => {
    const share = useSelector(selectShareSettings);
    const { audio } = useSelector(selectMain);
    const { answers, download, filters, reports, cross } = share;
    const { id: surveyId, defaultLang, multiLang } = useSelector(selectActiveSurvey);
    const withFiles = useSelector(selectWithFiles);
    const questions = useSelector(selectReportsQuestions);
    const responsesLimit = useSelector(selectResponsesLimit);
    const fetchPublic = useSelector(selectFetchPublic);
    const filtersVisible = useSelector(selectFiltersVisible);
    const comparisonEnabled = useSelector(selectComparisonEnabled);
    const { globalFilters, filterGroup } = useDeepSelector(selectReports);
    // Сравнение доступно, если разрешено настройкой ссылки или зашито в сохранённую группу фильтров.
    const comparisonAllowed =
        filters &&
        (Boolean(share.comparison) ||
            Boolean(Array.isArray(filterGroup?.comparePeriods) && filterGroup.comparePeriods.length));

    const commonRoute = useRouteMatch(PublicResultsRoutes.common.link);
    const singleRoute = useRouteMatch(PublicResultsRoutes.single.link);
    const singleIdRoute = useRouteMatch(PublicResultsRoutes.answer.link);
    const common = reports && commonRoute;
    const single = answers && singleRoute;
    const showTabs = Number(reports) + Number(answers) > 1;
    const enabled = Number(reports) + Number(answers) > 0;

    const requestQuestions = useAsync(getQuestionsForReports);
    const resetFilters = useAction(Actions.resetFilters);
    const getWordStatRequest = useAsync(getWordStat);
    const getComparisonWordStatRequest = useAsync(getComparisonWordStat);
    const getCrossTabListRequest = useAsync(getCrossTabList);

    const {
        params: {
            share: { filters: showFilters },
        },
    } = useSurveyParamsAdapter();

    const context = useMemo(
        () => ({
            onRemove: null,
            resetFilters,
            globalFilters,
            share,
            surveyId,
            showFilters,
        }),
        [globalFilters, resetFilters, share, showFilters, surveyId],
    );

    useEffect(() => {
        if (surveyId && Boolean(commonRoute)) {
            requestQuestions({
                surveyId,
                globalFilters,
                responsesLimit,
                page: 1,
                initialCount: 3,
            });
        }
    }, [surveyId, Boolean(commonRoute)]);

    const tabs: SlibTabsItems = useMemo(() => {
        const r = [];
        reports &&
            r.push({
                link: PublicResultsRoutes.common.link + window.location.search,
                name: 'Сводный отчет',
            });
        answers &&
            r.push({
                link: PublicResultsRoutes.single.link + window.location.search,
                name: 'Отдельные ответы',
            });
        return r;
    }, [answers, reports]);

    useEffect(() => {
        if (questions.length) {
            getWordStatRequest({
                questionIds: questions.map((item) => item.id),
                questions,
                start: formatDateForFilters(globalFilters.dateFrom),
                end: formatDateForFilters(globalFilters.dateTo),
                status: globalFilters.reportStatus,
                globalFilters,
            });
            if (cross) {
                getCrossTabListRequest({ surveyId });
            }
        }
    }, [globalFilters.dateTo, globalFilters.dateFrom, globalFilters.reportStatus, questions]);

    useEffect(() => {
        if (!comparisonEnabled || !questions.length) {
            return;
        }

        const cloudQuestions = questions.filter((question) => CLOUD_QUESTION_TYPES.includes(question.type));

        if (cloudQuestions.length) {
            getComparisonWordStatRequest({
                questionIds: cloudQuestions.map(({ id }) => id),
                questions,
            });
        }
    }, [comparisonEnabled, getComparisonWordStatRequest, questions]);

    useEffect(() => {
        // TODO use Redirect
        const handled = singleRoute || commonRoute || singleIdRoute;

        if (!handled) {
            if (reports) {
                PublicResultsRoutes.common.go();
            } else if (answers) {
                PublicResultsRoutes.single.go();
            }
        }
    }, [answers, commonRoute, reports, singleIdRoute, singleRoute]);

    useEffect(() => {
        if (!fetchPublic) {
            setTimeout(() => {
                parent && parent.postMessage('scrollTop', '*');
            }, 1000);
        }
    }, [fetchPublic]);

    const hasTestsOnFewPages = useMemo(() => {
        const pages = {};

        questions.forEach((item) => {
            if (TEST_QUESTIONS.includes(item.type)) {
                pages[item.question.pageId] = true;
            }
        });

        return Object.keys(pages).length > 1;
    }, [questions]);

    const hasTestQuestions = useMemo(() => {
        let result = false;

        questions?.forEach((item) => {
            if (TypesWithScore.has(item.type)) {
                result = true;
            }
        });

        return result;
    }, [questions]);

    if (fetchPublic) {
        return <Loader />;
    }

    if (!surveyId || !share || !enabled) {
        return <NotFound />;
    }

    return (
        <ResultsContext.Provider value={context}>
            <CustomStylesCtx.Provider value={customStylesInitial}>
                <div className={cn('public', css.wrap, { [css.withFilters]: filtersVisible })}>
                    <ResultsLayoutHeader
                        hasTestQuestions={hasTestQuestions}
                        hasTestsOnFewPages={hasTestsOnFewPages}
                        hideSingle={true}
                        showAudio={download && audio}
                        showDateFilter={filters}
                        showDownload={download}
                        showFilesOptions={download && withFiles}
                        showFilters={filters}
                        surveyId={surveyId}
                        isPublic={true}
                        compareAllowed={comparisonAllowed}
                        defaultLang={defaultLang}
                        multiLang={multiLang}
                    />
                    {showTabs && <SlimTabs tabs={tabs} />}
                    <div className={cn(css.tab, { [css.visible]: common })}>
                        <Route component={PublicCommon} path={PublicResultsRoutes.common.link} />
                    </div>
                    <div className={cn(css.tab, { [css.visible]: single })}>
                        <Route component={PublicSingle} path={singleRoutes} />
                    </div>
                    {filters && (
                        <AppliedFilters
                            headerHeight={16}
                            visible={filtersVisible}
                            isPublic={true}
                            filterGroup={filterGroup}
                        />
                    )}
                </div>
            </CustomStylesCtx.Provider>
        </ResultsContext.Provider>
    );
};
